import { useState, useEffect, useRef } from 'react'
import { useServices } from 'cng-web-lib'
import { FileForUserGetPartyId } from 'src/common/FileForUserCommon'
import TprApiUrls from 'src/apiUrls/TprApiUrls'
import { MPCI_ADMIN_ORG_ID } from '../constants/mpciConstants'

/**
 * useOrgFilter
 *
 * Centralises the "admin sees all, others see own" logic for MPCI.
 * Fetches org list from TPR service (same as other modules: POST TprApiUrls.GET).
 *
 * Fetch strategy:
 *   admin     — fetch ALL orgs (size: 9999) for the org filter dropdown
 *   non-admin — fetch ONLY their own org by id, lightweight single-record call
 *
 * Returns:
 *   isAdmin          {boolean}       — true when logged-in party === MPCI_ADMIN_ORG_ID
 *   orgList          {Array}         — [{ id, name }] — always populated (used for name lookup in datatables)
 *   selectedOrgId    {number|null}   — null = All Organisations (admin only)
 *   setSelectedOrgId {fn}
 *   effectiveOrgId   {number|null}   — resolved orgId to send to BE:
 *                                      null  = admin selected "All" (no filter)
 *                                      own partyId = non-admin (always scoped)
 */
function useOrgFilter() {
  const { securedSendRequest } = useServices()
  // FileForUserGetPartyId() respects "file for user" impersonation.
  // Falls back to logged-in user's own partyId if no impersonation active.
  const loggedInOrgId = Number(FileForUserGetPartyId()) || null
  const isAdmin = loggedInOrgId === MPCI_ADMIN_ORG_ID

  const [orgList, setOrgList] = useState([])
  const [selectedOrgId, setSelectedOrgId] = useState(null) // null = "All" (admin only)
  const mountedRef = useRef(true)

  useEffect(() => {
    mountedRef.current = true

    const body = isAdmin
      ? // Admin — fetch all orgs for the org filter dropdown
        { page: 0, size: 9999 }
      : // Non-admin — fetch only their own org by id, lightweight single-record call
        { page: 0, size: 1, filters: [{ field: 'id', operator: 'EQUAL', value: loggedInOrgId }] }

    securedSendRequest.execute(
      'POST',
      TprApiUrls.GET,
      body,
      (response) => {
        if (!mountedRef.current) return
        const content = response?.data?.content ?? []
        setOrgList(content.map((p) => ({ id: p.id, name: p.name })))
      },
      (err) => {
        if (!mountedRef.current) return
        console.error('[useOrgFilter] failed to load org list from TPR', err)
      }
    )

    return () => { mountedRef.current = false }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Non-admin always sends their own orgId; admin sends selected or null (all)
  const effectiveOrgId = isAdmin ? selectedOrgId : loggedInOrgId

  return { isAdmin, orgList, selectedOrgId, setSelectedOrgId, effectiveOrgId }
}

export default useOrgFilter
